<script>
  import { onMount, onDestroy, setContext } from "svelte";
  import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";

  import { mapbox, key } from "../mapbox.js";
  import {
    yardMarkers,
    loadAddresCoordinate,
    quoteSelectedMaterial,
    yards,
    tripCalculations,
    loadAddressText,
    customerQuote,
    activeRouteSchedule,
    showCustomerQuoteWindow,
  } from "../../stores.js";
  import { fetchYards } from "../../data.js";
  import { removeLines } from "./distance.js";

  import LoadAddress from "./LoadSite/LoadAddress.svelte";
  import TripCalculate from "./LoadSite/TripCalculate.svelte";
  import CustomerQuote from "./CustomerQuote.svelte";
  import RouteScheduleModal from "./RouteScheduleModal.svelte";

  /**
   * Provide Mapbox instance getter to child components (e.g. Yard markers, overlays)
   * via Svelte context.
   */
  setContext(key, {
    getMap: () => map,
  });

  /** @type {number} Latitude coordinate for map center initialization */
  export let lat;
  /** @type {number} Longitude coordinate for map center initialization */
  export let lon;
  /** @type {number} Initial map zoom level */
  export let zoom;

  /** @type {HTMLDivElement|undefined} DOM container element reference for Mapbox GL */
  let container;
  /** @type {mapboxgl.Map|undefined} Mapbox map instance */
  let map;
  /** @type {mapboxgl.Marker|undefined} Mapbox marker instance for the selected load/site address */
  let loadAddressMarker;
  /** @type {boolean} State flag indicating if the load address / quote planner window is minimized/hidden */
  let hideLoadAddressWindow = false;
  /** @type {boolean} State flag indicating if the calculate haulage window is minimized/hidden (default: true) */
  let hideCalculateWindow = true;
  /** @type {boolean} State flag controlling if calculate window is enabled (disabled until routes appear) */
  let disableCalculate = true;
  /** @type {number} Count of mapped routes */
  let routesCount = 0;

  /** @type {boolean} State flag indicating if the customer quote window is minimized/hidden (default: false as it is always enabled) */
  let hideCustomerQuoteWindow = false;

  $: if ($showCustomerQuoteWindow) {
    hideCustomerQuoteWindow = false;
  }

  function openCustomerQuote() {
    hideCustomerQuoteWindow = false;
    $showCustomerQuoteWindow = true;
  }

  function onCopyToQuote(e) {
    // When a haulage package is copied, automatically reveal the customer quote window!
    hideCustomerQuoteWindow = false;
    $showCustomerQuoteWindow = true;
  }

  function onRoutesDisplayed(e) {
    console.log("Routes displayed on map:", e.detail);
    routesCount = e.detail?.count || 0;
    disableCalculate = false;
    // Once all routes finished displaying, close quote window and open calculate haulage window
    hideLoadAddressWindow = true;
    hideCalculateWindow = false;
  }

  function onRoutesCleared() {
    disableCalculate = true;
  }

  function openQuotePlanner() {
    hideLoadAddressWindow = false;
    hideCalculateWindow = true;
  }

  function openHaulageCalculator() {
    hideCalculateWindow = false;
    hideLoadAddressWindow = true;
  }

  /**
   * Dynamically scales custom map icon markers by setting a CSS variable (`--map-icon-size`)
   * on the map container based on the current zoom level.
   *
   * @returns {void}
   */
  function updateIconScale() {
    if (!map || !container) return;
    const z = map.getZoom();
    const size = z < 5 ? 12.5 : z < 10 ? 25 : z < 15 ? 50 : 100;
    container.style.setProperty("--map-icon-size", size + "px");
  }

  /**
   * Initializes the Mapbox GL map instance, centers it at [lon, lat], sets initial zoom,
   * configures marker scaling, and binds zoom event listeners.
   *
   * @returns {void}
   */
  function load() {
    if (map || !container) return;
    const hasInitialCoords =
      $loadAddresCoordinate &&
      Array.isArray($loadAddresCoordinate) &&
      $loadAddresCoordinate.length === 2;
    map = new mapbox.Map({
      container,
      style: "mapbox://styles/mapbox/streets-v9",
      center: hasInitialCoords ? $loadAddresCoordinate : [lon, lat],
      zoom: hasInitialCoords ? 12 : zoom,
    });
    updateIconScale();
    map.on("zoom", updateIconScale);

    if (hasInitialCoords) {
      const popup = new mapbox.Popup({ offset: 25 }).setText(
        $loadAddressText || "Load site",
      );
      const el = document.createElement("div");
      el.className = "marker map-icon-marker";
      el.style.backgroundImage = "url(./assests/green_pin.svg)";
      el.style.cursor = "pointer";

      loadAddressMarker = new mapbox.Marker(el)
        .setLngLat($loadAddresCoordinate)
        .setPopup(popup)
        .addTo(map);
    }
  }

  onMount(() => {
    if (!map && container) {
      load();
    }
  });

  /**
   * Handles the 'address-change' custom event emitted when a new load site address is selected.
   * Clears any existing load site marker, updates the `$loadAddresCoordinate` store,
   * and creates a new custom Mapbox marker with a popup at the new coordinates.
   *
   * @param {CustomEvent<{detail: {result: {geometry: {coordinates: [number, number]}}}}>} e - Address change event containing geocoded location data.
   * @returns {void}
   */
  function onAddressChange(e) {
    if (loadAddressMarker) {
      loadAddressMarker.remove();
    }
    const result = e.detail?.detail?.result || e.detail?.result;
    const placeName = result?.place_name || "";
    const coords = result?.geometry?.coordinates;
    if (!coords) return;

    const popup = new mapbox.Popup({ offset: 25 }).setText(placeName || "Load site");
    $loadAddresCoordinate = coords;
    $loadAddressText = placeName;

    const el = document.createElement("div");
    el.className = "marker map-icon-marker";
    el.style.backgroundImage = "url(./assests/green_pin.svg)";
    el.style.cursor = "pointer";

    loadAddressMarker = new mapbox.Marker(el)
      .setLngLat($loadAddresCoordinate)
      .setPopup(popup)
      .addTo(map);

    //addDistanceLines(map, $loadAddresCoordinate, $yardMarkers);
  }

  /**
   * Removes the load site address marker and clears any plotted distance lines from the map.
   *
   * @returns {void}
   */
  function removeAddress() {
    loadAddressMarker.remove();
    removeLines(map);
  }

  /**
   * Filters and sorts the list of yards stored in `$yards` based on the selected material (`$quoteSelectedMaterial`).
   * 1. Filters yards that accept the selected material and have available pricing.
   * 2. Sorts filtered yards in ascending order by the lowest unit price for that material.
   * 3. Appends any unpriced or remaining yards to the end of the list.
   * 4. Updates the reactive `$yards` store with the reordered array.
   *
   * @returns {void}
   */
  function updateYardList() {
    // Filter yards by material name
    const filteredYards = $yards.filter((yard) => {
      return yard.accepts.some(
        (material) =>
          material.name === $quoteSelectedMaterial &&
          material.prices.length > 0,
      );
    });

    // Sort filtered yards by cheapest price, putting yards with no price at the end
    filteredYards.sort((a, b) => {
      const getPrice = (yard) => {
        const material = yard.accepts.find(
          (material) => material.name === $quoteSelectedMaterial,
        );
        if (material && material.prices && material.prices.length > 0) {
          return Math.min(
            ...material.prices.map((price) => parseFloat(price.price)),
          );
        } else {
          // If no price array or it's empty, consider it as the highest price
          return Infinity;
        }
      };
      return getPrice(a) - getPrice(b);
    });

    // the above drops any yeards without pricing, we'll put them back in at the end
    $yards.forEach((obj1) => {
      if (!filteredYards.some((obj2) => obj2.id === obj1.id)) {
        filteredYards.push(obj1);
      }
    });

    $yards = filteredYards;
  }

  /**
   * Attaches calculated trip/route load data (from `$tripCalculations`) to their respective yards in `$yards`,
   * then sorts all yards in ascending order based on total price per tonne (`load.costs.total_tonne_price`).
   * Yards without valid calculation data are pushed to the end.
   *
   * @returns {void}
   */
  function addTripCalculationsToYards() {
    let loads = $tripCalculations.loads;
    loads.forEach((load) => {
      const yard = $yards.find((yard) => yard.id === load.yard.id);
      if (yard) {
        yard.load = load;
      }
    });
    // Sort filtered yards by total tonne price
    let sortedYars = $yards;
    sortedYars.sort((a, b) => {
      if (a.load && b.load) {
        if (a.load.costs.total_tonne_price && b.load.costs.total_tonne_price) {
          return (
            a.load.costs.total_tonne_price - b.load.costs.total_tonne_price
          );
        } else {
          return Infinity;
        }
      } else {
        return Infinity;
      }
    });
    $yards = sortedYars;
  }

  /**
   * Svelte lifecycle hook executed on component destruction.
   * Cleans up and destroys the Mapbox map instance to prevent memory leaks.
   */
  onDestroy(() => {
    if (map) map.remove();
  });

  /**
   * Sets the visibility state of the Load Address / Quote Planner overlay window.
   *
   * @param {CustomEvent<boolean>|{detail: boolean}} event - Event containing the new boolean visibility state (`true` to hide, `false` to show).
   * @returns {void}
   */
  function setLoadAddressWindow(event) {
    console.log("setLoadAddressWindow: ", hideLoadAddressWindow);
    hideLoadAddressWindow = event.detail;
  }
</script>

<!-- this special element will be explained in a later section -->
<svelte:head>
  <link
    rel="stylesheet"
    href="https://unpkg.com/mapbox-gl/dist/mapbox-gl.css"
    on:load={load}
  />
</svelte:head>

<div class="mapbox-root">
  <div
    bind:this={container}
    class="mapbox-map-canvas"
    style="--map-icon-size: {zoom < 5
      ? '12.5px'
      : zoom < 10
        ? '25px'
        : zoom < 15
          ? '50px'
          : '100px'};"
  ></div>

  {#if map}
    <slot />
    <!-- Quote & Route Planner window -->
    <div class="quote-planner-wrapper" hidden={hideLoadAddressWindow}>
      <LoadAddress
        on:address-change={(e) => onAddressChange(e)}
        on:clear-address={removeAddress}
        {map}
        on:yards-list-updated={updateYardList}
        on:routes-displayed={onRoutesDisplayed}
        on:routes-cleared={onRoutesCleared}
        on:hideLoadAddressWindow={setLoadAddressWindow}
      />
    </div>

    <!-- Calculate Haulage window -->
    <div class="calculate-planner-wrapper" hidden={hideCalculateWindow}>
      <TripCalculate
        {map}
        {disableCalculate}
        {routesCount}
        on:trip-calculated={addTripCalculationsToYards}
        on:copy-to-quote={onCopyToQuote}
        on:close-calculate-window={() => (hideCalculateWindow = true)}
        on:open-quote-window={openQuotePlanner}
      />
    </div>

    <!-- Customer Quote window (Always enabled) -->
    <div class="customer-quote-wrapper" hidden={hideCustomerQuoteWindow}>
      <CustomerQuote
        on:close-quote-window={() => {
          hideCustomerQuoteWindow = true;
          $showCustomerQuoteWindow = false;
        }}
        on:open-calculator={() => {
          hideCalculateWindow = false;
        }}
      />
    </div>

    <!-- Floating Map Controls (Left: Quote Planner & Haulage Calculator) -->
    <div class="map-floating-controls map-floating-controls-left">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      {#if hideLoadAddressWindow}
        <button
          class="map-control-btn"
          on:click={openQuotePlanner}
          type="button"
        >
          <ChevronRight size={18} />
          <span>Open Quote Planner</span>
        </button>
      {/if}

      <!-- svelte-ignore a11y-click-events-have-key-events -->
      {#if hideCalculateWindow}
        <button
          class="map-control-btn {disableCalculate ? '' : 'btn-ready'}"
          on:click={openHaulageCalculator}
          type="button"
        >
          <ChevronRight size={18} />
          <span>Open Haulage Calculator</span>
          {#if !disableCalculate}
            <span
              class="tt-badge tt-badge-success"
              style="font-size: 10px; padding: 1px 6px;">Ready</span
            >
          {/if}
        </button>
      {/if}
    </div>

    <!-- Floating Map Controls (Right: Customer Quote Window) -->
    {#if hideCustomerQuoteWindow}
      <div class="map-floating-controls map-floating-controls-right">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <button
          class="map-control-btn {$customerQuote.haulagePackages?.length > 0 ||
          $customerQuote.haulagePackage
            ? 'btn-ready'
            : ''}"
          on:click={openCustomerQuote}
          type="button"
        >
          <ChevronLeft size={18} />
          <span>Open Customer Quote</span>
          {#if $customerQuote.haulagePackages?.length > 0 || $customerQuote.haulagePackage}
            <span
              class="tt-badge tt-badge-success"
              style="font-size: 10px; padding: 1px 6px;">Ready</span
            >
          {/if}
        </button>
      </div>
    {/if}

    <!-- Turnaround Route Schedule Modal Dialog -->
    {#if $activeRouteSchedule}
      <RouteScheduleModal
        scheduleData={$activeRouteSchedule}
        on:close={() => ($activeRouteSchedule = null)}
      />
    {/if}
  {/if}
</div>

<style>
  .mapbox-root {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .mapbox-map-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  .quote-planner-wrapper,
  .calculate-planner-wrapper,
  .customer-quote-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 100;
  }

  .map-floating-controls {
    position: absolute;
    top: 1rem;
    z-index: 110;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    pointer-events: auto;
  }

  .map-floating-controls-left {
    left: 1rem;
  }

  .map-floating-controls-right {
    right: 1rem;
  }

  .map-control-btn {
    background: #ffffff;
    color: var(--tt-slate-700);
    border: 1px solid var(--tt-slate-200);
    border-radius: 9999px;
    padding: 8px 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    box-shadow: var(--tt-shadow-md);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .map-control-btn:hover {
    background: var(--tt-slate-50);
    color: var(--tt-primary);
    box-shadow: var(--tt-shadow-lg);
    transform: translateY(-1px);
  }

  .map-control-btn.btn-ready {
    border-color: var(--tt-success-border);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
  }

  /* Route Turnaround Schedule Mapbox Popup Styling */
  :global(.mapboxgl-popup.tt-route-schedule-mapbox-popup) {
    z-index: 50;
  }

  :global(.tt-route-schedule-mapbox-popup .mapboxgl-popup-content) {
    padding: 0;
    border-radius: 10px;
    box-shadow:
      0 10px 25px -5px rgba(0, 0, 0, 0.25),
      0 8px 10px -6px rgba(0, 0, 0, 0.15);
    border: 1px solid #cbd5e1;
    overflow: hidden;
    font-family: inherit;
    background: #ffffff;
  }

  :global(.tt-route-schedule-mapbox-popup .mapboxgl-popup-close-button) {
    font-size: 16px;
    color: #64748b;
    padding: 4px 8px;
    top: 6px;
    right: 6px;
    z-index: 5;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  :global(.tt-route-schedule-mapbox-popup .mapboxgl-popup-close-button:hover) {
    background-color: #f1f5f9;
    color: #0f172a;
  }

  :global(.tt-route-popup-card) {
    display: flex;
    flex-direction: column;
    width: 380px;
    max-width: 90vw;
  }

  :global(.tt-popup-header) {
    padding: 10px 14px 8px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  :global(.tt-popup-title) {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }

  :global(.tt-popup-badges) {
    display: flex;
    gap: 6px;
    margin-top: 3px;
    align-items: center;
  }

  :global(.tt-popup-suburb) {
    font-size: 10.5px;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    padding: 1px 6px;
    border-radius: 3px;
  }

  :global(.tt-popup-material) {
    font-size: 10.5px;
    font-weight: 600;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    padding: 1px 6px;
    border-radius: 3px;
  }

  :global(.tt-popup-kpi-bar) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 6px 12px;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
  }

  :global(.tt-kpi-pill) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #334155;
    background: #f8fafc;
    padding: 2px 7px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
  }

  :global(.tt-popup-table-container) {
    max-height: 210px;
    overflow-y: auto;
    padding: 0;
  }

  :global(.tt-popup-table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    text-align: left;
  }

  :global(.tt-popup-table thead th) {
    position: sticky;
    top: 0;
    background: #f8fafc;
    color: #475569;
    font-weight: 700;
    padding: 6px 8px;
    border-bottom: 1px solid #e2e8f0;
    z-index: 2;
  }

  :global(.tt-popup-table td) {
    padding: 5px 8px;
    border-bottom: 1px solid #f1f5f9;
  }

  :global(.popup-dir-badge) {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 3px;
  }

  :global(.popup-dir-badge.outbound) {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  :global(.popup-dir-badge.inbound) {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
  }

  :global(.td-p-toll.has-toll) {
    color: #b91c1c;
    font-weight: 700;
  }

  :global(.td-p-toll.is-free) {
    color: #15803d;
    font-weight: 600;
  }

  :global(.td-p-toll.is-error) {
    color: #b91c1c;
    font-weight: 800;
    cursor: help;
  }

  :global(.tt-popup-footer) {
    padding: 8px 12px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: center;
  }

  :global(.tt-popup-expand-btn) {
    width: 100%;
    background: #ffffff;
    border: 1px solid #2563eb;
    color: #2563eb;
    font-size: 11.5px;
    font-weight: 700;
    padding: 5px 10px;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  :global(.tt-popup-expand-btn:hover) {
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
  }
</style>
